import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Table,
  Modal,
  Spin,
  Tabs,
  message,
  Checkbox,
  Form,
  Descriptions,
  Divider,
  Alert
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  GlobalOutlined,
  SyncOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import * as XLSX from 'xlsx';
import formatCurrency from '../../utils/formatCurrency';
import { getExternalVoucherExpiryDate } from '../../utils/externalVoucherExpiry';
import { normalizeTermsForDisplay } from '../../utils/termsAndConditionsDisplay';
import {
  buildOrderCardSerialNumber,
  getPrimaryOrderSerialNumber,
  resolveOrderSerialBase,
} from '../../utils/orderSerialNumber';
import '../../styles/richtext.css';

const { Text, Title, Paragraph } = Typography;

const POLL_INTERVAL_MS = 10_000;
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;

const OrderDetails = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState('0'); // Default select first voucher type
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportSelectedKeys, setExportSelectedKeys] = useState([]);
  const [exportSelectedFields, setExportSelectedFields] = useState(['Order Number', 'Voucher Name', 'Brand', 'Code', 'Serial Number', 'PIN', 'Status', 'Expiry Date', 'Issued At']);
  const [activeStatus, setActiveStatus] = useState('all'); // Status filter
  // Field definitions (English)
  const exportFields = [
    { label: 'Order Number', value: 'Order Number' },
    { label: 'Voucher Name', value: 'Voucher Name' },
    { label: 'Brand', value: 'Brand' },
    { label: 'Code', value: 'Code' },
    { label: 'Serial Number', value: 'Serial Number' },
    { label: 'PIN', value: 'PIN' },
    { label: 'Status', value: 'Status' },
    { label: 'Expiry Date', value: 'Expiry Date' },
    { label: 'Issued At', value: 'Issued At' }
  ];

  // Default select all voucher types when opening export modal
  const openExportModal = () => {
    setExportSelectedKeys(voucherCodesData.map((_, idx) => idx.toString()));
    setExportSelectedFields(exportFields.map(f => f.value));
    setExportModalVisible(true);
  };



  const pollingRef = useRef(null);
  const pollStartRef = useRef(null);

  const isPendingExternalOrder = useCallback((o) => {
    return o && o.status === 'pending' && o.isExternalVoucherOrder;
  }, []);

  /** Provider callback fail reason: order.failureReason, then statusInfo, then last callbackHistory.failReason */
  const resolveOrderFailureReason = useCallback((o) => {
    if (!o) return '';
    const fr = o.failureReason;
    if (fr != null && String(fr).trim()) return String(fr).trim();
    const desc = o.statusInfo?.description;
    // Pending orders use generic description "Checking balance and inventory" — not a failure; do not treat as callback error.
    if (desc != null && String(desc).trim() && /^Order failed:/i.test(String(desc))) {
      const stripped = String(desc).replace(/^Order failed:\s*/i, '').trim();
      if (stripped && stripped !== 'Unknown error') return stripped;
    }
    const hist = Array.isArray(o.externalOrderData?.callbackHistory)
      ? o.externalOrderData.callbackHistory
      : [];
    for (let i = hist.length - 1; i >= 0; i -= 1) {
      const entry = hist[i];
      if (entry?.failReason != null && String(entry.failReason).trim()) {
        return String(entry.failReason).trim();
      }
    }
    return '';
  }, []);

  const fetchOrderDetails = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`/orders/${orderId}`);
      const orderData = response.data.order || response.data;
      setOrder(orderData);

      if (orderData?.items?.length > 0) {
        setActiveTabKey('0');
      }

      return orderData;
    } catch (error) {
      console.error('Fetch order details error:', error);
      if (!silent) message.error('Failed to load order details');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  useEffect(() => {
    if (isPendingExternalOrder(order)) {
      if (!pollStartRef.current) pollStartRef.current = Date.now();

      pollingRef.current = setInterval(async () => {
        if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          return;
        }

        const updated = await fetchOrderDetails(true);
        if (updated && updated.status !== 'pending') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          if (updated.status === 'completed') {
            const zc =
              String(updated.externalOrderData?.zhongminFulfillmentType || '').toLowerCase() ===
              'zhichong';
            message.success(
              zc ? 'Direct recharge order completed.' : 'Voucher codes have been delivered!',
            );
          }
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- poll only when pending status changes
  }, [order?.status, order?.isExternalVoucherOrder, isPendingExternalOrder, fetchOrderDetails]);



  const getCodeStatusColor = (status) => {
    const colors = {
      'available': 'green',
      'sold-not-redeemed': 'orange',
      'redeemed': 'blue',
      'expired': 'red',
      'issued': 'green',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  const getCodeStatusLabel = (status) => {
    const labels = {
      'available': 'Available',
      'sold-not-redeemed': 'Sold',
      'redeemed': 'Redeemed',
      'expired': 'Expired',
      'issued': 'Issued',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getOrderStatusDisplay = (status) => {
    if (status === 'completed') {
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>
      );
    }
    if (status === 'pending') {
      return (
        <Tag color="blue" icon={<SyncOutlined spin />}>Processing</Tag>
      );
    }
    if (status === 'failed') {
      return (
        <Tag color="red">Failed</Tag>
      );
    }
    return <Tag>{status}</Tag>;
  };

  // Robust copy helper: navigator.clipboard requires a secure context, and
  // can throw when the document isn't focused. Fall back to the legacy
  // textarea+execCommand path so insecure (HTTP) deployments still work.
  const safeCopyToClipboard = async (text) => {
    if (text === undefined || text === null) return false;
    const value = String(text);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch (err) {
      console.error('navigator.clipboard.writeText failed:', err);
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      return false;
    }
  };

  // Copy order number
  const handleCopyOrderNumber = async () => {
    if (!order?.orderNumber) return;
    const ok = await safeCopyToClipboard(order.orderNumber);
    if (ok) {
      message.success('Order number copied');
    } else {
      message.error('Could not copy automatically — please copy manually');
    }
  };



  // Export selected voucher type codes to multi-sheet Excel (export all if none selected)
  const exportSelectedVoucherCodesToExcel = () => {
    const voucherCodesData = processVoucherCodesData();
    let exportGroups = voucherCodesData;
    if (exportSelectedKeys.length > 0) {
      exportGroups = voucherCodesData.filter((_, idx) => exportSelectedKeys.includes(idx.toString()));
    }
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    
    // Track used worksheet names for uniqueness
    const usedSheetNames = new Set();
    
    // Create sheet for each voucher type
    exportGroups.forEach((voucherData, index) => {
      if (!voucherData?.codes?.length) return;
      
      // Determine column names by voucher type
      const hasSerialNumber = voucherData.codes.some(code => code.serialNumber);
      const hasPIN = voucherData.codes.some(code => code.pin);
      const hasExpiryDate = voucherData.codes.some(code => code.expiryDate);
      
      const headers = [
        'Order Number',
        'Voucher Name', 
        'Brand',
        'Code',
        ...(hasSerialNumber ? ['Serial Number'] : []),
        ...(hasPIN ? ['PIN'] : []),
        'Status',
        ...(hasExpiryDate ? ['Expiry Date'] : []),
        'Issued At'
      ];
      
      const rows = voucherData.codes.map(code => [
        order?.orderNumber || '',
        voucherData.voucherName || '',
        voucherData.voucherBrand || '',
        code.code || '',
        ...(hasSerialNumber ? [code.serialNumber || ''] : []),
        ...(hasPIN ? [code.pin || ''] : []),
        code.status || '',
        ...(hasExpiryDate ? [code.expiryDate ? dayjs(code.expiryDate).format('YYYY-MM-DD') : ''] : []),
        code.issuedAt ? dayjs(code.issuedAt).format('YYYY-MM-DD HH:mm:ss') : ''
      ]);
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      
      // Set column width
      const colWidths = headers.map(header => ({ wch: Math.max(header.length, 15) }));
      worksheet['!cols'] = colWidths;
      
      // Generate unique sheet name
      let baseSheetName = voucherData.voucherName || `Voucher_${index + 1}`;
      // Clean worksheet name, remove Excel-unsupported characters
      baseSheetName = baseSheetName.replace(/[\\/?*[\]:]/g, '_');
      // Limit worksheet name length (Excel limit 31 chars)
      if (baseSheetName.length > 31) {
        baseSheetName = baseSheetName.substring(0, 28) + '...';
      }
      
      let sheetName = baseSheetName;
      let counter = 1;
      
      // Ensure worksheet name uniqueness
      while (usedSheetNames.has(sheetName)) {
        const suffix = `_${counter}`;
        const maxLength = 31 - suffix.length;
        const truncatedBase = baseSheetName.length > maxLength ? 
          baseSheetName.substring(0, maxLength) : baseSheetName;
        sheetName = `${truncatedBase}${suffix}`;
        counter++;
      }
      
      usedSheetNames.add(sheetName);
      
      // Add sheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
    
    // Export file
    const fileName = `${order?.orderNumber || 'order'}_voucher_codes.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    message.success(`Exported ${exportGroups.length} voucher types to Excel`);
    setExportModalVisible(false);
  };

  // Fix grouping: unique group by voucherId+voucherName+brand, include full voucher info
  const processVoucherCodesData = () => {
    if (!order) return [];
    const voucherCodesData = [];
    if (order.voucherInstances && order.voucherInstances.length > 0) {
      const groupedInstances = {};
      order.voucherInstances.forEach((instance, index) => {
        // Ensure voucherId is always a string
        const voucherId = String(
          instance.voucherDetails?.voucherId?._id || 
          instance.voucherDetails?.voucherId || 
          instance.voucherDetails?._id || 
          instance.voucherId || 
          instance.voucherDetails?.id || 
          'unknown'
        );
        
        const voucherName = instance.voucherDetails?.voucherId?.name || instance.voucherDetails?.voucherName || instance.voucherDetails?.name || instance.voucherName || 'Unknown Voucher';
        const voucherBrand = instance.voucherDetails?.voucherId?.brand || instance.voucherDetails?.voucherBrand || instance.voucherBrand || 'Unknown Brand';
        const groupKey = `${voucherId}__${voucherName}__${voucherBrand}`;
        
        if (!groupedInstances[groupKey]) {
          // Collect full voucher info
          const voucherDetails = instance.voucherDetails || {};
          groupedInstances[groupKey] = {
            voucherId,
            voucherName,
            voucherBrand,
            // Add full voucher info
            voucherInfo: {
              images: voucherDetails.images || [],
              category: voucherDetails.category || '',
              country: voucherDetails.country || '',
              basePrice: voucherDetails.basePrice || 0,
              clientPrice: voucherDetails.clientPrice || voucherDetails.basePrice || 0, // Prefer tier price
              currency: voucherDetails.currency || '',
              clientCurrency: voucherDetails.clientCurrency || voucherDetails.currency || '',
              description: voucherDetails.description || '',
              termsAndConditions: normalizeTermsForDisplay(
                voucherDetails.termsAndConditions ??
                  voucherDetails.voucherId?.termsAndConditions ??
                  '',
              ),
              validityPeriod: voucherDetails.validityPeriod || '',
              minimumOrderQuantity: voucherDetails.minimumOrderQuantity || 1,
              maximumOrderQuantity: voucherDetails.maximumOrderQuantity || 1000,
              isActive: voucherDetails.isActive || false,
              voucherStatus: voucherDetails.voucherStatus || 'unknown'
            },
            codes: []
          };
        }
        groupedInstances[groupKey].codes.push({
          code: instance.voucherCode,
          serialNumber: instance.voucherCodeId?.serialNumber || instance.serialNumber,
          pin: instance.voucherCodeId?.pin || instance.pin,
          redemptionUrl: instance.redemptionUrl || null,
          expiryDate: instance.voucherCodeId?.expiryDate || instance.expiryDate,
          status: instance.status,
          issuedAt: instance.issuedAt
        });
      });
      Object.values(groupedInstances).forEach(group => {
        voucherCodesData.push(group);
      });
    }

    // Fallback for external provider orders: read cards directly from externalOrderData
    if (voucherCodesData.length === 0 && order.isExternalVoucherOrder) {
      const firstItem = Array.isArray(order.items) ? order.items[0] : null;
      const voucherId =
        firstItem?.voucherId ||
        firstItem?.externalVoucherData?.externalId ||
        'external';
      const voucherName = firstItem?.externalVoucherData?.name || 'External Voucher';
      const voucherBrand =
        firstItem?.externalVoucherData?.brand ||
        order?.externalOrderData?.type ||
        'External';

      const fallbackCodes = [];
      const externalOrderNo = resolveOrderSerialBase(
        order?.externalOrderData?.orderNo,
        order?.externalOrderData?.order_id,
        order?.externalOrderData?.serialNumber,
      );

      const providerRedeemUrl = order?.externalOrderData?.providerResult?.url || null;

      if (Array.isArray(order?.externalOrderData?.cards) && order.externalOrderData.cards.length > 0) {
        order.externalOrderData.cards.forEach((card, index) => {
          const code = card?.code || card?.cards || card?.number;
          if (!code) return;
          const cardUrl = card?.url || card?.redemptionUrl || null;
          fallbackCodes.push({
            code,
            serialNumber: buildOrderCardSerialNumber(externalOrderNo, index),
            pin: card?.pin || card?.password || null,
            redemptionUrl: cardUrl || (index === 0 ? providerRedeemUrl : null),
            expiryDate: getExternalVoucherExpiryDate({
              expiryDate: card?.expiryDate,
              deadline: card?.deadline,
            }),
            status: 'issued',
            issuedAt: order?.timestamps?.processedAt || order?.createdAt,
          });
        });
      } else {
        const singleCode = order?.externalOrderData?.secret_code || order?.externalOrderData?.egift_exchangecode;
        if (singleCode) {
          fallbackCodes.push({
            code: singleCode,
            serialNumber:
              order?.externalOrderData?.serialNumber ||
              getPrimaryOrderSerialNumber(externalOrderNo),
            pin: null,
            redemptionUrl: providerRedeemUrl,
            expiryDate: getExternalVoucherExpiryDate({
              availableEnd: order?.externalOrderData?.available_end,
            }),
            status: 'issued',
            issuedAt: order?.timestamps?.processedAt || order?.createdAt,
          });
        }
      }

      if (fallbackCodes.length > 0) {
        voucherCodesData.push({
          voucherId: String(voucherId),
          voucherName,
          voucherBrand,
          voucherInfo: {
            images: [],
            category: firstItem?.externalVoucherData?.category || '',
            country: firstItem?.externalVoucherData?.country || '',
            basePrice: firstItem?.unitPrice || 0,
            clientPrice: firstItem?.unitPrice || 0,
            currency: firstItem?.externalVoucherData?.currency || order?.currency || '',
            clientCurrency: firstItem?.externalVoucherData?.currency || order?.currency || '',
            description: firstItem?.externalVoucherData?.description || '',
            termsAndConditions: '',
            validityPeriod: '',
            minimumOrderQuantity: 1,
            maximumOrderQuantity: 1000,
            isActive: true,
            voucherStatus: 'online',
            voucherType: 'External Voucher',
          },
          codes: fallbackCodes,
        });
      }
    }
    return voucherCodesData;
  };

  // Generate voucher code table column config (reference admin)
  const getVoucherCodeColumns = () => [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (code) => (
        <Text copyable style={{ fontSize: '12px' }}>{code}</Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => (
        <Tag color={getCodeStatusColor(status)} size="small">
          {getCodeStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 150,
      render: (serial) => (
        <Text style={{ fontSize: '12px' }}>
          {serial ? <Text code>{serial}</Text> : '-'}
        </Text>
      ),
    },
    {
      title: 'PIN',
      dataIndex: 'pin',
      key: 'pin',
      width: 120,
      render: (pin) => (
        <Text style={{ fontSize: '12px' }}>
          {pin ? <Text code>{pin}</Text> : '-'}
        </Text>
      ),
    },
    {
      title: 'Redemption link',
      dataIndex: 'redemptionUrl',
      key: 'redemptionUrl',
      width: 200,
      render: (url) =>
        url && /^https?:\/\//i.test(String(url).trim()) ? (
          <Typography.Link href={String(url).trim()} target="_blank" rel="noopener noreferrer">
            Open link
          </Typography.Link>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {date ? dayjs(date).format('MMM DD, YYYY') : '-'}
        </Text>
      ),
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      width: 120,
      render: (date) => (
        <Text style={{ fontSize: '12px' }}>
          {date ? dayjs(date).format('MMM DD, YYYY') : '-'}
        </Text>
      ),
    }
  ];

  // Status stats consistent with admin (click to toggle)
  const renderVoucherCodeStats = (voucherData) => {
    const codes = voucherData.codes || [];
    const stats = {
      total: codes.length,
      ready: codes.filter(c => c.status === 'available' || c.status === 'issued' || c.status === 'sold-not-redeemed').length,
      redeemed: codes.filter(c => c.status === 'redeemed').length,
      expired: codes.filter(c => c.status === 'expired').length
    };
    const statusList = [
      { key: 'all', label: `All: ${stats.total}`, color: '#1890ff' },
      { key: 'ready', label: `Ready: ${stats.ready}`, color: '#52c41a' },
      { key: 'redeemed', label: `Redeemed: ${stats.redeemed}`, color: '#1890ff' },
      { key: 'expired', label: `Expired: ${stats.expired}`, color: '#ff4d4f' },
    ];
    return (
      <div style={{ 
        marginBottom: 8, 
        padding: '8px 0', 
        background: 'none', 
        borderRadius: '6px', 
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
      }}>
        {/* Status labels - right */}
        <div style={{ display: 'flex', gap: 8 }}>
          {statusList.map(stat => (
            <Tag
              key={stat.key}
              color={activeStatus === stat.key ? stat.color : '#f0f0f0'}
              style={{
                cursor: 'pointer',
                fontWeight: activeStatus === stat.key ? 700 : 400,
                border: activeStatus === stat.key ? `2px solid ${stat.color}` : '1px solid #e0e0e0',
                background: activeStatus === stat.key ? stat.color : '#f0f0f0',
                color: activeStatus === stat.key ? '#fff' : '#333',
                transition: 'all 0.2s',
                minWidth: 70,
                textAlign: 'center',
                fontSize: 12,
                boxShadow: activeStatus === stat.key ? '0 2px 8px rgba(24,144,255,0.08)' : 'none',
              }}
              onClick={() => setActiveStatus(stat.key)}
              onMouseEnter={e => e.currentTarget.style.border = `2px solid ${stat.color}`}
              onMouseLeave={e => e.currentTarget.style.border = activeStatus === stat.key ? `2px solid ${stat.color}` : '1px solid #e0e0e0'}
            >
              {stat.label}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <Card style={{ margin: '24px' }}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Text>Order not found</Text>
        </div>
      </Card>
    );
  }

  const voucherCodesData = processVoucherCodesData();
  // Amount display priority
  const amount = order.totalAmount ?? order.accountDeduction ?? order.amount ?? 0;

  // Build purchased items list in e-commerce style
  const items = Array.isArray(order.items) ? order.items : [];
  const findGroupByVoucherId = (vid) => {
    const key = String(vid);
    return (voucherCodesData || []).find(g => String(g.voucherId) === key);
  };

  const purchasedItems = items.map((item, idx) => {
    const group = findGroupByVoucherId(item.voucherId);
    const isExternal = item.isExternalVoucher;
    const name = isExternal ? (item.externalVoucherData?.name || 'Voucher') : (group?.voucherName || 'Voucher');
    const brand = isExternal ? (item.externalVoucherData?.brand || '') : (group?.voucherBrand || '');
    const image = group?.voucherInfo?.images?.[0] || null;
    return {
      key: `${String(item.voucherId)}-${idx}`,
      voucherId: item.voucherId,
      name,
      brand,
      image,
      quantity: item.quantity || 0,
      unitPrice: item.unitPrice || 0,
      totalPrice: item.totalPrice || ((item.unitPrice || 0) * (item.quantity || 0))
    };
  });

  return (
    <div style={{ padding: 24, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Back */}
      <div style={{ marginBottom: 12 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/client/orders')}
          style={{ padding: '0 16px', height: 38, fontSize: 15 }}
        >
          Back
        </Button>
      </div>

      {/* Async voucher delivery banner */}
      {isPendingExternalOrder(order) && (() => {
        const isZhichong =
          String(order.externalOrderData?.zhongminFulfillmentType || '').toLowerCase() ===
          'zhichong';
        const rechargeAccount = order.externalOrderData?.rechargeAccount || null;
        return (
          <Alert
            message={isZhichong ? 'Direct recharge in progress' : 'Voucher codes are being processed'}
            description={
              isZhichong
                ? `Your direct-recharge order has been placed successfully${
                    rechargeAccount ? ` for account ${rechargeAccount}` : ''
                  }. The top-up will be credited to the recharge account within a few minutes — no voucher codes are issued for direct recharge. This page will update automatically once the provider confirms.`
                : 'Your order has been placed successfully. Voucher codes will be delivered within a few minutes. This page will update automatically.'
            }
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        );
      })()}

      {/* Direct-recharge completion banner (no voucher codes will ever appear, so we surface success explicitly). */}
      {order.status === 'completed' &&
        order.isExternalVoucherOrder &&
        String(order.externalOrderData?.zhongminFulfillmentType || '').toLowerCase() ===
          'zhichong' && (
          <Alert
            message="Direct recharge completed"
            description={(() => {
              const acct = order.externalOrderData?.rechargeAccount || null;
              return acct
                ? `The amount has been credited to recharge account ${acct}. No voucher codes are issued for direct-recharge orders.`
                : 'The amount has been credited to the recharge account. No voucher codes are issued for direct-recharge orders.';
            })()}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

      {order.status === 'pending' &&
        order.isExternalVoucherOrder &&
        resolveOrderFailureReason(order) && (
        <Alert
          message="Provider callback — action may be required"
          description={resolveOrderFailureReason(order)}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {order.status === 'failed' && (
        <Alert
          message="Order failed"
          description={
            resolveOrderFailureReason(order) ||
            'Order processing failed. Please contact support for details.'
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Header: Order number, status, total */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Title level={4} style={{ margin: 0 }}>{order.orderNumber}</Title>
              <CopyOutlined onClick={handleCopyOrderNumber} style={{ fontSize: 16, cursor: 'pointer', color: '#888' }} />
              {getOrderStatusDisplay(order.status)}
            </div>
            <div style={{ color: '#888', marginTop: 6 }}>
              Order Date {order.createdAt ? dayjs(order.createdAt).format('MMM DD, YYYY HH:mm') : '--'}
            </div>
          </Col>
          <Col>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#27c346' }}>
              {formatCurrency(amount, order.clientCurrency || (order.client && order.client.currency) || order.currency)}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Main two-column layout */}
      <Row gutter={16}>
        {/* Left: Purchased items */}
        <Col xs={24} md={16}>
          <Card title="Purchased Items" style={{ marginBottom: 16 }}>
            {purchasedItems.length === 0 ? (
              <Text type="secondary">No items found</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {purchasedItems.map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', background: '#fafafa', border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {item.image ? (
                        <img alt={item.name} src={item.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <GlobalOutlined style={{ fontSize: 24, color: '#bbb' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{item.brand}</div>
                      <div style={{ color: '#555', marginTop: 6 }}>
                        Qty: {item.quantity}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#555' }}>
                        {formatCurrency(item.unitPrice, order.currency)}
                        <span style={{ color: '#999', marginLeft: 4 }}>/ea</span>
                      </div>
                      <div style={{ fontWeight: 600 }}>
                        {formatCurrency(item.totalPrice, order.currency)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Voucher codes grouped (existing) */}
          <div style={{ marginTop: 16 }}>
      {voucherCodesData.length > 0 ? (
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Voucher Codes</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={openExportModal}
                >
                  Export Codes
                </Button>
                <Text type="secondary" style={{ fontSize: '14px', marginLeft: 16 }}>
                  {voucherCodesData.length} voucher type{voucherCodesData.length > 1 ? 's' : ''}
                </Text>
              </div>
            </div>
          }
          size="small"
            style={{ marginBottom: 8, boxShadow: 'none' }}
        >
          {/* Voucher type navigation bar */}
          <Tabs
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            type="card"
              style={{ marginBottom: '8px' }}
            items={voucherCodesData.map((voucherData, idx) => ({
              key: idx.toString(),
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{voucherData.voucherName}</span>
                  <Tag size="small" color="blue">{voucherData.codes.length}</Tag>
                </div>
              ),
              children: (
                <div>
                  {/* Status stats bar */}
                  {renderVoucherCodeStats(voucherData)}
                  {/* Voucher codes table */}
                  <Table
                      dataSource={
                        activeStatus === 'all'
                          ? voucherData.codes
                          : voucherData.codes.filter(c =>
                              activeStatus === 'ready'
                                ? (c.status === 'available' || c.status === 'issued' || c.status === 'sold-not-redeemed')
                                : c.status === activeStatus
                            )
                      }
                    columns={getVoucherCodeColumns()}
                    rowKey={(record, index) => `${voucherData.voucherId}-${index}`}
                    pagination={false}
                    size="small"
                    scroll={{ x: 800 }}
                  />
                </div>
              )
            }))}
          />
          {/* Export Modal */}
          <Modal
            title="Export Codes"
            open={exportModalVisible}
            onCancel={() => setExportModalVisible(false)}
            onOk={exportSelectedVoucherCodesToExcel}
            okText="Export"
            cancelText="Cancel"
          >
            <Form layout="vertical">
              <Form.Item label="Select voucher types to export">
                <Checkbox.Group
                  style={{ width: '100%' }}
                  value={exportSelectedKeys}
                  onChange={setExportSelectedKeys}
                >
                  <Row gutter={[0, 8]}>
                    {voucherCodesData.map((voucher, idx) => (
                      <Col span={24} key={idx}>
                        <Checkbox value={idx.toString()}>
                          {voucher.voucherName} ({voucher.codes.length} codes)
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item label="Select fields to export">
                <Checkbox.Group
                  style={{ width: '100%' }}
                  value={exportSelectedFields}
                  onChange={setExportSelectedFields}
                >
                  <Row gutter={[0, 8]}>
                    {exportFields.map(field => (
                      <Col span={12} key={field.value}>
                        <Checkbox value={field.value}>{field.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </Form>
          </Modal>
        </Card>
        ) : null}
          </div>
        </Col>

        {/* Right: Transaction + Summary */}
        <Col xs={24} md={8}>
          <Card title="Transaction Details" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Payment Method">Account Balance</Descriptions.Item>
              {order.accountDeduction?.transactionId && (
                <Descriptions.Item label="Transaction ID">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Text code style={{ fontSize: 12 }}>{String(order.accountDeduction.transactionId)}</Text>
                    <Button
                      size="small"
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={async () => {
                        const ok = await safeCopyToClipboard(
                          order.accountDeduction.transactionId
                        );
                        if (ok) {
                          message.success('Transaction ID copied');
                        } else {
                          message.error(
                            'Could not copy automatically — please copy manually'
                          );
                        }
                      }}
                    />
                  </span>
                </Descriptions.Item>
              )}
              {order.accountDeduction?.deductedAt && (
                <Descriptions.Item label="Deducted At">{dayjs(order.accountDeduction.deductedAt).format('MMM DD, YYYY HH:mm')}</Descriptions.Item>
              )}
              <Descriptions.Item label="From Top-Up">
                {formatCurrency(order.accountDeduction?.fromTopUp || 0, order.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="From Credit">
                {formatCurrency(order.accountDeduction?.fromCredit || 0, order.currency)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Order Summary">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Subtotal</Text>
                <Text>{formatCurrency(order.subtotal || 0, order.currency)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Tax</Text>
                <Text>{formatCurrency((order.tax && order.tax.amount) || 0, order.currency)}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <Text>Total</Text>
                <Text>{formatCurrency(order.totalAmount || amount || 0, order.currency)}</Text>
              </div>
              {order.notes && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">Notes</Text>
                  <Paragraph style={{ marginBottom: 0 }}>{order.notes}</Paragraph>
                </div>
              )}
            </div>
          </Card>

          {order.isExternalVoucherOrder && order.externalOrderData && (
            <Card title="External Order" style={{ marginTop: 16 }}>
              <Descriptions column={1} size="small">
                {order.externalOrderData.type && (
                  <Descriptions.Item label="Provider">{order.externalOrderData.type}</Descriptions.Item>
                )}
                {order.externalOrderData.order_id && (
                  <Descriptions.Item label="Provider Order ID">{order.externalOrderData.order_id}</Descriptions.Item>
                )}
                {(() => {
                  const summary = order.externalOrderData.fulfillmentSummary || {};
                  const providerName = String(
                    order.externalOrderData.type ||
                      order.externalOrderData.providerName ||
                      ''
                  ).toLowerCase();
                  // Count delivered cards from order data; treat "Empty" as not yet fulfilled
                  // for providers (gowabi/giftee) that use the literal placeholder.
                  const cardsArr = Array.isArray(order.externalOrderData.cards)
                    ? order.externalOrderData.cards
                    : [];
                  const realCardCount = cardsArr.filter((c) => {
                    const code = c?.code || c?.cards || c?.number;
                    if (!code) return false;
                    if (providerName === 'gowabi' || providerName === 'giftee') {
                      return String(code) !== 'Empty';
                    }
                    return true;
                  }).length;
                  const instanceCount = Array.isArray(order.voucherInstances)
                    ? order.voucherInstances.length
                    : 0;
                  const fallbackSingleCode =
                    order.externalOrderData.secret_code ||
                    order.externalOrderData.egift_exchangecode
                      ? 1
                      : 0;
                  const itemsQtySum = Array.isArray(order.items)
                    ? order.items.reduce(
                        (sum, it) => sum + (Number(it?.quantity) || 0),
                        0,
                      )
                    : 0;

                  const requestedCount =
                    Number(summary.requestedCount) > 0
                      ? Number(summary.requestedCount)
                      : Number(order.externalOrderData.expectedCardsCount) > 0
                        ? Number(order.externalOrderData.expectedCardsCount)
                        : itemsQtySum > 0
                          ? itemsQtySum
                          : 0;

                  const successCount =
                    Number(summary.successCount) > 0
                      ? Number(summary.successCount)
                      : Math.max(realCardCount, instanceCount, fallbackSingleCode);

                  const failedCount =
                    Number.isFinite(Number(summary.failedCount)) &&
                    Number(summary.failedCount) > 0
                      ? Number(summary.failedCount)
                      : Math.max(0, requestedCount - successCount);

                  const failureReason = summary.failureReason || null;

                  if (requestedCount === 0 && successCount === 0 && !failureReason) {
                    return null;
                  }

                  return (
                    <Descriptions.Item label="Fulfillment">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Text>
                          Success: {successCount} / {requestedCount}
                        </Text>
                        <Text type={failedCount > 0 ? 'warning' : undefined}>
                          Failed: {failedCount}
                        </Text>
                        {failureReason && (
                          <Text type="secondary">Reason: {failureReason}</Text>
                        )}
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {order.externalOrderData.refund?.amount > 0 && (
                  <Descriptions.Item label="Refunded for Failed Cards">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Text>{formatCurrency(order.externalOrderData.refund.amount, order.currency)}</Text>
                      {order.externalOrderData.refund.reason && (
                        <Text type="secondary">{order.externalOrderData.refund.reason}</Text>
                      )}
                    </div>
                  </Descriptions.Item>
                )}
                {(order.externalOrderData.secret_code || order.externalOrderData.egift_exchangecode) && (
                  <Descriptions.Item label="Secret Code">
                    <Text code style={{ fontSize: 12 }}>{order.externalOrderData.secret_code || order.externalOrderData.egift_exchangecode}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Col>
      </Row>

    </div>
  );
};

export default OrderDetails;
